import { styled } from "@mui/material/styles";
import {
  AcquireBookDataGrid,
  buildSelfAcquiredInitialState,
  OTHER_USER_RESOURCE_FIELD,
  SELF_ACQUIRED_OPERATOR_VALUE,
  OTHER_USER_ACQUIRED_OPERATOR_VALUE,
} from "@/components/DataGrid";
import {
  type GridColDef,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridValueFormatterParams,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { isRequestCompleted, statusToLabel } from "@/features/allMatters/utils";
import { type RequestDetailAndRealEstates } from "@/features/allMatters/types";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import React, {
  forwardRef,
  type ForwardRefRenderFunction,
  useImperativeHandle,
  useContext,
  useEffect,
  useState,
} from "react";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { formatNumberWithComma } from "@/utils/formatNumber";
import OutputMenu from "@/features/allMatters/components/OutputMenu";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { userDataContext } from "@/hooks/useUserData";
import { useShowSendToSamboKun } from "@/features/partner/hooks/useShowSendToSamboKun";
import { useSamboKunLimit } from "@/features/partner/hooks/useSamboKunLimit";
import {
  getSamboKunSendDisableReason,
  samboKunSendDisabledTooltip,
} from "@/features/partner/utils/isSendDisabled";
import { SendToSamboKunDialog } from "@/features/partner/components/SendToSamboKunDialog";

interface AllMattersListGridRow extends RequestDetailAndRealEstates {
  action?: boolean;
}

interface AllMattersListAPIResponse {
  count: number;
  list: AllMattersListGridRow[];
}

const otherUserResourceFilterOperators: Array<
  GridFilterOperator<AllMattersListGridRow, boolean>
> = [
  {
    label: "自分で取得",
    value: SELF_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        !row.isOtherUserResource,
  },
  {
    label: "他ユーザー取得",
    value: OTHER_USER_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        row.isOtherUserResource,
  },
];

const allMattersAnalyzeListContractSchema = z
  .object({
    list: z.array(
      z
        .object({
          isOtherUserResource: z.boolean(),
          hasTriggerPictureRequest: z.boolean(),
        })
        .passthrough()
    ),
  })
  .passthrough();

/**
 * 全部事項解析結果一覧APIのレスポンスが契約を満たしているか検証する。
 * @param payload APIレスポンスのペイロード
 * @param endpoint 呼び出し元のAPIエンドポイント
 * @throws {Error} API契約違反がある場合に例外を送出する
 */
const validateAllMattersAnalyzeListContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = allMattersAnalyzeListContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  isReadOnly: boolean,
  isSharableUser: boolean,
  onDownloadComplete: () => void,
  showSendToSamboKun: boolean,
  onClickSendToSamboKun: (rowId: number) => void,
  maxPropertiesPerRequest: number | null,
  isLimitError: boolean
): GridColDef[] => {
  const BASE_ACTION_COLUMN_WIDTH = 240;
  const SAMBO_KUN_BUTTON_WIDTH = 150;
  const actionColumnMinWidth =
    BASE_ACTION_COLUMN_WIDTH +
    (showSendToSamboKun ? SAMBO_KUN_BUTTON_WIDTH : 0);

  return [
    {
      field: "taskId",
      headerName: "識別番号",
      width: 110,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "identificationName",
      headerName: "識別名",
      width: 280,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({
        value,
      }: GridRenderCellParams<string, AllMattersListGridRow>) => value ?? "",
    },
    {
      field: "requestedCount",
      headerName: "アップロード件数",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, AllMattersListGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "succeededCount",
      headerName: "成功件数",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, AllMattersListGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "解析依頼日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: OTHER_USER_RESOURCE_FIELD,
      headerName: "他ユーザー取得",
      width: 140,
      filterOperators: otherUserResourceFilterOperators,
      hide: !isSharableUser,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: () => "",
      renderCell: ({
        value,
      }: GridRenderCellParams<boolean, AllMattersListGridRow>) =>
        value ? <CheckIcon color="action" fontSize="small" /> : "",
    },
    {
      field: "status",
      headerName: "ステータス",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        return statusToLabel(value);
      },
    },
    {
      field: "analysisOptions",
      headerName: "解析オプション",
      width: 200,
      sortable: false,
      align: "left",
      headerAlign: "left",
      valueGetter: (
        params: GridValueGetterParams<any, AllMattersListGridRow>
      ) => {
        const options = [];
        if (params.row.includeTitleSection) options.push("表題部");
        if (params.row.includeKoku) options.push("甲区");
        if (params.row.includeOtsuku) options.push("乙区");
        return options.length > 0 ? options.join(", ") : "-";
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: actionColumnMinWidth,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<string, AllMattersListGridRow>) => {
        const isOutputEnabled =
          isRequestCompleted(row.status) && row.succeededCount > 0;
        return (
          <Stack direction="row" spacing={2}>
            <LinkButton
              disabled={!isRequestCompleted(row.status)}
              variant={ButtonVariantOption.Contained}
              wrapperSize={SizingWrapperStyle.INHERIT}
              href={`/allmatters/detail/${id}/`}
            >
              詳細
            </LinkButton>
            <OutputMenu
              disabled={!isOutputEnabled}
              requestId={id.valueOf() as number}
              isReadOnly={isReadOnly}
              isPropertyDetailOptionCredit={row.isPropertyDetailOptionCredit}
              onDownloadComplete={onDownloadComplete}
            />
            {showSendToSamboKun
              ? (() => {
                  const reason = getSamboKunSendDisableReason(
                    row.succeededCount,
                    maxPropertiesPerRequest,
                    isLimitError
                  );
                  const isDisabled = reason !== "ok";
                  const tooltipText = !row.hasTriggerPictureRequest
                    ? "不動産登記取得結果に紐づく解析のみ連携できます"
                    : isDisabled
                    ? samboKunSendDisabledTooltip[reason]
                    : "";
                  return (
                    <Tooltip
                      title={tooltipText}
                      placement="top"
                      arrow
                      // limit-loading は空文字にマップされるため空タイトル時は
                      // 空ポッパー描画を避けるためホバーを無効化する
                      disableHoverListener={
                        (row.hasTriggerPictureRequest && !isDisabled) ||
                        tooltipText === ""
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          disabled={
                            !isOutputEnabled ||
                            !row.hasTriggerPictureRequest ||
                            isDisabled
                          }
                          onClick={() => {
                            onClickSendToSamboKun(id.valueOf() as number);
                          }}
                        >
                          参謀くんへ連携
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()
              : null}
          </Stack>
        );
      },
    },
  ];
};

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<{ reload: () => void }> = (
  _props,
  ref
) => {
  const isReadOnly = useReadOnlyUser();
  const userData = useContext(userDataContext);
  const isSharableUser = Boolean(userData.data.isSharable);
  const selfAcquiredInitialState =
    buildSelfAcquiredInitialState(isSharableUser);
  const showSendToSamboKun = useShowSendToSamboKun();
  const { maxPropertiesPerRequest, isError: isLimitError } = useSamboKunLimit({
    enabled: showSendToSamboKun,
  });
  const [samboKunDialogRowId, setSamboKunDialogRowId] = useState<number | null>(
    null
  );
  // LaunchDarkly フラグや SSO 連携状態がランタイムで OFF に変わった場合、
  // 開きっぱなしのダイアログから送信できてしまわないよう state を強制クリアする
  useEffect(() => {
    if (!showSendToSamboKun) {
      setSamboKunDialogRowId(null);
    }
  }, [showSendToSamboKun]);
  let rows: AllMattersListGridRow[] = [];
  const { apiClient } = useApiClient();

  const fetcher = async (url: string): Promise<AllMattersListAPIResponse> => {
    const { data } = await apiClient.get<AllMattersListAPIResponse>(url);
    validateAllMattersAnalyzeListContract(data, url);
    return data;
  };

  const { data, isLoading, error, mutate } = useSWR<
    AllMattersListAPIResponse,
    Error
  >("/all-matters-analyze/", fetcher, {
    revalidateOnFocus: true,
    refreshInterval: 60000,
    dedupingInterval: 30000,
  });

  const colDef = makeGridColDef(
    isReadOnly,
    isSharableUser,
    () => {
      mutate().catch(() => {
        // エラーハンドリングは既にuseSWRで行われているため、ここでは何もしない
      });
    },
    showSendToSamboKun,
    (rowId) => {
      setSamboKunDialogRowId(rowId);
    },
    maxPropertiesPerRequest,
    isLimitError
  );

  // refを使って外部公開する関数を定義する
  useImperativeHandle(
    ref,
    () => {
      return {
        // mutate()を使って再読み込みを行う
        async reload() {
          await mutate().then((r) => {
            if (r) rows = r.list;
          });
        },
      };
    },
    []
  );

  if (error != null && data === undefined) {
    return (
      <Stack alignItems="center">
        <Typography color="error" sx={{ my: "20px" }}>
          データの取得に失敗しました。
        </Typography>
      </Stack>
    );
  }

  if (isLoading || data === undefined) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ my: "20px" }} />
      </Stack>
    );
  }

  rows = data.list;

  return (
    <>
      <StyledDataGrid
        stickyHeader={true}
        autoHeight
        disableSelectionOnClick={true}
        rows={rows}
        columns={colDef}
        initialState={selfAcquiredInitialState}
        sortModel={[
          {
            field: "id",
            sort: "asc",
          },
        ]}
      />
      {showSendToSamboKun && samboKunDialogRowId != null && (
        <SendToSamboKunDialog
          open={samboKunDialogRowId != null}
          onClose={() => {
            setSamboKunDialogRowId(null);
          }}
          target="allMatters"
          analyzeRequestId={samboKunDialogRowId}
        />
      )}
    </>
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const FeedGrid = forwardRef<{ reload: () => void }>(_FeedGrid);
